import { CheckCircle2, Circle, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Progress } from '@/components/ui/progress'

const SECTION_LABELS: Record<string, string> = {
  skills: 'Skills',
  goals: 'Goals',
  obstacles: 'Obstacles',
  commitments: 'Commitments',
  role_preferences: 'Roles',
  communication_prefs: 'Communication',
  tool_familiarity: 'Tools',
  meeting_prefs: 'Meetings',
  boundaries: 'Boundaries',
  leadership_preference: 'Leadership',
  conflict_decision_prefs: 'Conflict',
}

interface SectionProgressBarProps {
  sectionOrder: string[]
  completedSections: Record<string, string>
  activeSectionKey: string | null
  disabledSections?: string[]
  onSectionClick?: (key: string) => void
}

export function SectionProgressBar({
  sectionOrder,
  completedSections,
  activeSectionKey,
  disabledSections = [],
  onSectionClick,
}: SectionProgressBarProps) {
  const totalSections = sectionOrder.length
  const completedCount = sectionOrder.filter((k) => Boolean(completedSections[k])).length
  const progressPct = totalSections > 0 ? Math.round((completedCount / totalSections) * 100) : 0

  return (
    <div className='w-full space-y-3'>
      {/* Overall progress */}
      <div className='flex items-center justify-between text-sm'>
        <span className='font-medium text-gray-700'>Prep Progress</span>
        <span className='text-gray-500'>
          {completedCount} / {totalSections} sections
        </span>
      </div>
      <Progress value={progressPct} className='h-2' />

      {/* Per-section dots */}
      <TooltipProvider>
        <div className='flex flex-wrap gap-2'>
          {sectionOrder.map((key) => {
            const isDone = Boolean(completedSections[key])
            const isActive = key === activeSectionKey
            const isDisabled = disabledSections.includes(key)
            const label = SECTION_LABELS[key] ?? key

            return (
              <Tooltip key={key}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => !isDisabled && onSectionClick?.(key)}
                    disabled={isDisabled}
                    aria-label={`${label} — ${isDone ? 'completed' : isActive ? 'active' : 'not started'}`}
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium transition-all',
                      isDone
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : isActive
                          ? 'bg-indigo-50 border-indigo-400 text-indigo-700 ring-2 ring-indigo-300 ring-offset-1'
                          : isDisabled
                            ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-default'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer'
                    )}
                  >
                    {isDisabled ? (
                      <Lock className='h-3 w-3' />
                    ) : isDone ? (
                      <CheckCircle2 className='h-3 w-3 text-emerald-500' />
                    ) : (
                      <Circle className='h-3 w-3 text-gray-400' />
                    )}
                    <span>{label}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>
                  {isDone
                    ? `Completed: ${new Date(completedSections[key]).toLocaleString()}`
                    : isDisabled
                      ? 'Section disabled by professor'
                      : isActive
                        ? 'Currently discussing'
                        : 'Not yet started'}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      </TooltipProvider>
    </div>
  )
}
